import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Braces, Check, FileSpreadsheet, Info, Search, Type, User } from "lucide-react";
import {
  CAMPAIGN_CONTACT_FIELD_KEYS,
  type CampaignContactFieldKey,
  type CampaignMessageCoverageEntry,
  type CampaignVariableMappingEntry,
} from "@multi-chat/shared";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { cn } from "../../lib/utils.js";
import {
  contactsModalBodyClass,
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts/contacts-dialog-presentation.js";
import { CampaignModalContent } from "./CampaignModalContent.js";
import { CampaignCoverageIndicator } from "./CampaignPersonalizationSection.js";
import {
  mappingCoverageSourceKey,
  mappingEntryLabel,
  variableTokenLabel,
  type CampaignMessageSourceType,
  type CampaignVariableUsage,
} from "./campaign-message-presentation.js";

const CONSTANT_MAX = 1024;

const SOURCE_ICONS = {
  CONTACT_FIELD: User,
  UPLOAD_COLUMN: FileSpreadsheet,
  CONSTANT: Type,
} as const;

const optionRowClass =
  "flex min-w-0 cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-[0.8125rem] transition-colors hover:bg-surface-muted has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--color-ring)]";

function ExcerptWithToken({ excerpt, token }: { excerpt: string; token: string }): React.JSX.Element {
  const index = excerpt.indexOf(token);
  if (index < 0) return <>{excerpt}</>;
  return (
    <>
      {excerpt.slice(0, index)}
      <mark className="rounded bg-warning-muted px-0.5 font-semibold text-ink">{token}</mark>
      {excerpt.slice(index + token.length)}
    </>
  );
}

/**
 * Configure one Campaign variable. Only the three contract sources exist:
 * Contact field (fixed set), CSV column, fixed text. Apply updates the local
 * draft only; Save Message persists.
 */
export function CampaignVariableSourceDialog({
  open,
  parameterKey,
  initial,
  uploadColumns,
  usages,
  excerpt,
  coverage,
  sampleValueFor,
  canEdit = true,
  onOpenChange,
  onSave,
  onCloseAutoFocus,
}: {
  open: boolean;
  parameterKey: string;
  initial: CampaignVariableMappingEntry | null;
  uploadColumns: readonly string[];
  usages: readonly CampaignVariableUsage[];
  excerpt: string | null;
  coverage: CampaignMessageCoverageEntry | null;
  sampleValueFor: (entry: CampaignVariableMappingEntry) => string | null;
  canEdit?: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (entry: CampaignVariableMappingEntry) => void;
  onCloseAutoFocus?: (event: Event) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [source, setSource] = useState<CampaignMessageSourceType>("CONTACT_FIELD");
  const [contactField, setContactField] = useState<CampaignContactFieldKey>("firstName");
  const [uploadColumn, setUploadColumn] = useState("");
  const [constantValue, setConstantValue] = useState("");
  const [fieldQuery, setFieldQuery] = useState("");
  const [columnQuery, setColumnQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setFieldQuery("");
    setColumnQuery("");
    if (initial == null) {
      setSource("CONTACT_FIELD");
      setContactField("firstName");
      setUploadColumn(uploadColumns[0] ?? "");
      setConstantValue("");
      return;
    }
    switch (initial.source) {
      case "CONTACT_FIELD":
        setSource("CONTACT_FIELD");
        setContactField(initial.field);
        setUploadColumn(uploadColumns[0] ?? "");
        setConstantValue("");
        break;
      case "UPLOAD_COLUMN":
        setSource("UPLOAD_COLUMN");
        setUploadColumn(initial.column);
        setContactField("firstName");
        setConstantValue("");
        break;
      case "CONSTANT":
        setSource("CONSTANT");
        setConstantValue(initial.value);
        setContactField("firstName");
        setUploadColumn(uploadColumns[0] ?? "");
        break;
      default: {
        const _exhaustive: never = initial;
        return _exhaustive;
      }
    }
  }, [open, initial, uploadColumns]);

  const candidate: CampaignVariableMappingEntry | null = (() => {
    switch (source) {
      case "CONTACT_FIELD":
        return { source: "CONTACT_FIELD", field: contactField };
      case "UPLOAD_COLUMN":
        return uploadColumn.trim().length > 0
          ? { source: "UPLOAD_COLUMN", column: uploadColumn.trim() }
          : null;
      case "CONSTANT":
        return constantValue.trim().length > 0 && constantValue.length <= CONSTANT_MAX
          ? { source: "CONSTANT", value: constantValue.trim() }
          : null;
      default: {
        const _exhaustive: never = source;
        return _exhaustive;
      }
    }
  })();

  const contactFields = useMemo(() => {
    const query = fieldQuery.trim().toLowerCase();
    return CAMPAIGN_CONTACT_FIELD_KEYS.filter(
      (field) =>
        query.length === 0 ||
        t(`campaigns.message.fields.${field}`).toLowerCase().includes(query),
    );
  }, [fieldQuery, t]);

  const columns = useMemo(() => {
    const query = columnQuery.trim().toLowerCase();
    return uploadColumns.filter(
      (column) => query.length === 0 || column.toLowerCase().includes(query),
    );
  }, [columnQuery, uploadColumns]);

  const save = () => {
    if (!candidate || !canEdit) return;
    onSave(candidate);
    onOpenChange(false);
  };

  const token = variableTokenLabel(parameterKey);
  const usageLabels = usages.map((usage) =>
    t(`campaigns.content.components.${usage.componentType}`, {
      index: (usage.buttonIndex ?? 0) + 1,
    }),
  );
  const sample = candidate ? sampleValueFor(candidate) : null;
  const coverageMatches =
    coverage != null && candidate != null && coverage.mappedSource === mappingCoverageSourceKey(candidate);

  const sourceOptions: Array<{ value: CampaignMessageSourceType; label: string }> = [
    { value: "CONTACT_FIELD", label: t("campaigns.message.sources.contactField") },
    { value: "UPLOAD_COLUMN", label: t("campaigns.message.sources.uploadedField") },
    { value: "CONSTANT", label: t("campaigns.message.sources.fixedText") },
  ];

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <CampaignModalContent
        size="md"
        data-testid="campaign-variable-source-dialog"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <div className={cn(contactsModalHeaderClass, "grid gap-2")}>
          <div className="flex items-start gap-3">
            <span
              className="inline-grid size-9 shrink-0 place-items-center rounded-lg border border-line/70 bg-surface-muted text-ink"
              aria-hidden="true"
            >
              <Braces size={16} />
            </span>
            <div className="grid min-w-0 gap-1">
              <AlertDialogTitle>{t("campaigns.content.configureVariable")}</AlertDialogTitle>
              <p className="m-0 flex flex-wrap items-center gap-1.5 text-[0.75rem] text-muted">
                <span data-testid="campaign-variable-dialog-token">
                  <LtrValue className="rounded-md border border-line/70 bg-surface px-1.5 py-0.5 font-mono text-[0.75rem] font-semibold text-ink">
                    {token}
                  </LtrValue>
                </span>
                {usageLabels.length > 0 ? (
                  <span data-testid="campaign-variable-dialog-usage">
                    {usageLabels.length > 1
                      ? t("campaigns.content.usedInMany", { places: usageLabels.join(", ") })
                      : usageLabels[0]}
                  </span>
                ) : null}
              </p>
            </div>
          </div>
          {excerpt ? (
            <AlertDialogDescription className="m-0 rounded-md bg-surface-muted px-2.5 py-1.5 text-[0.75rem] leading-snug text-muted">
              <bdi>
                “<ExcerptWithToken excerpt={excerpt} token={token} />”
              </bdi>
            </AlertDialogDescription>
          ) : (
            <AlertDialogDescription className="m-0 text-[0.75rem] text-muted">
              {t("campaigns.message.mapVariableDescription")}
            </AlertDialogDescription>
          )}
        </div>

        <div className={cn(contactsModalBodyClass, "grid content-start gap-3")}>
          <fieldset className="m-0 grid gap-1.5 border-0 p-0">
            <legend className="mb-1.5 text-[0.75rem] font-medium text-muted">
              {t("campaigns.message.sourceType")}
            </legend>
            <div className="grid grid-cols-3 gap-1 rounded-lg border border-line/70 bg-surface-muted p-1">
              {sourceOptions.map((option) => {
                const disabled = !canEdit;
                const Icon = SOURCE_ICONS[option.value];
                const active = source === option.value;
                return (
                  <label
                    key={option.value}
                    className={cn(
                      "flex min-w-0 cursor-pointer items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-[0.75rem] font-medium transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[var(--color-ring)]",
                      active ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink",
                      disabled ? "cursor-default opacity-60" : null,
                    )}
                    data-testid={`campaign-variable-source-${option.value}`}
                  >
                    <input
                      type="radio"
                      name="campaign-variable-source"
                      className="sr-only"
                      checked={active}
                      disabled={disabled}
                      onChange={() => setSource(option.value)}
                    />
                    <Icon size={13} className="shrink-0" aria-hidden="true" />
                    <span className="truncate">{option.label}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>

          {source === "CONTACT_FIELD" ? (
            <fieldset className="m-0 grid gap-1.5 border-0 p-0">
              <legend className="sr-only">{t("campaigns.message.contactField")}</legend>
              <div className="flex items-center gap-2 rounded-md border border-line/70 bg-surface px-2.5">
                <Search size={13} className="shrink-0 text-muted" aria-hidden="true" />
                <input
                  type="search"
                  value={fieldQuery}
                  onChange={(event) => setFieldQuery(event.target.value)}
                  placeholder={t("campaigns.content.searchFields")}
                  aria-label={t("campaigns.content.searchFields")}
                  className="h-9 min-w-0 flex-1 border-0 bg-transparent text-[0.8125rem] outline-none"
                />
              </div>
              <div className="grid max-h-48 gap-0.5 overflow-y-auto overscroll-contain">
                {contactFields.length === 0 ? (
                  <p className="m-0 px-2.5 py-2 text-[0.8125rem] text-muted">
                    {t("campaigns.content.noFieldMatches")}
                  </p>
                ) : (
                  contactFields.map((field) => (
                    <label
                      key={field}
                      className={cn(optionRowClass, contactField === field ? "bg-accent-soft" : null)}
                    >
                      <input
                        type="radio"
                        name="campaign-variable-contact-field"
                        className="sr-only"
                        checked={contactField === field}
                        disabled={!canEdit}
                        onChange={() => setContactField(field)}
                      />
                      <User size={13} className="shrink-0 text-muted" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate">
                        {t(`campaigns.message.fields.${field}`)}
                      </span>
                      {contactField === field ? (
                        <Check size={14} className="shrink-0 text-accent" aria-hidden="true" />
                      ) : null}
                    </label>
                  ))
                )}
              </div>
            </fieldset>
          ) : null}

          {source === "UPLOAD_COLUMN" ? (
            uploadColumns.length === 0 ? (
              <p
                className="m-0 flex items-start gap-2 rounded-md border border-line/70 bg-surface-muted px-3 py-2.5 text-[0.8125rem] leading-snug text-muted"
                data-testid="campaign-variable-csv-unavailable"
              >
                <Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                {t("campaigns.content.csvUnavailable")}
              </p>
            ) : (
              <fieldset className="m-0 grid gap-1.5 border-0 p-0">
                <legend className="sr-only">{t("campaigns.message.uploadedField")}</legend>
                <div className="flex items-center gap-2 rounded-md border border-line/70 bg-surface px-2.5">
                  <Search size={13} className="shrink-0 text-muted" aria-hidden="true" />
                  <input
                    type="search"
                    value={columnQuery}
                    onChange={(event) => setColumnQuery(event.target.value)}
                    placeholder={t("campaigns.message.searchColumns")}
                    aria-label={t("campaigns.message.searchColumns")}
                    className="h-9 min-w-0 flex-1 border-0 bg-transparent text-[0.8125rem] outline-none"
                  />
                </div>
                <div className="grid max-h-48 gap-0.5 overflow-y-auto overscroll-contain">
                  {columns.length === 0 ? (
                    <p className="m-0 px-2.5 py-2 text-[0.8125rem] text-muted">
                      {t("campaigns.content.noColumnMatches")}
                    </p>
                  ) : (
                    columns.map((column) => (
                      <label
                        key={column}
                        className={cn(optionRowClass, uploadColumn === column ? "bg-accent-soft" : null)}
                      >
                        <input
                          type="radio"
                          name="campaign-variable-upload-column"
                          className="sr-only"
                          checked={uploadColumn === column}
                          disabled={!canEdit}
                          onChange={() => setUploadColumn(column)}
                        />
                        <FileSpreadsheet size={13} className="shrink-0 text-muted" aria-hidden="true" />
                        <span className="min-w-0 flex-1 truncate" dir="auto">
                          {column}
                        </span>
                        {uploadColumn === column ? (
                          <Check size={14} className="shrink-0 text-accent" aria-hidden="true" />
                        ) : null}
                      </label>
                    ))
                  )}
                </div>
              </fieldset>
            )
          ) : null}

          {source === "CONSTANT" ? (
            <div className="grid gap-1.5">
              <label className="text-[0.75rem] font-medium text-muted" htmlFor="campaign-constant-value">
                {t("campaigns.message.fixedText")}
              </label>
              <textarea
                id="campaign-constant-value"
                dir="auto"
                rows={3}
                maxLength={CONSTANT_MAX}
                disabled={!canEdit}
                className="w-full resize-y rounded-md border border-line/70 bg-surface px-3 py-2 text-start text-[0.8125rem] leading-snug focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]"
                value={constantValue}
                onChange={(event) => setConstantValue(event.target.value)}
                placeholder={t("campaigns.message.constantPlaceholder")}
              />
              <p className="m-0 text-end text-[0.7rem] tabular-nums text-subtle">
                {constantValue.length}/{CONSTANT_MAX}
              </p>
            </div>
          ) : null}

          {candidate ? (
            <div className="grid gap-2 border-t border-line/60 pt-3">
              <p className="m-0 flex flex-wrap items-baseline gap-1.5 text-[0.75rem]" data-testid="campaign-variable-sample">
                <span className="text-muted">{t("campaigns.content.sampleValue")}</span>
                {sample ? (
                  <span className="min-w-0 truncate font-medium text-ink" dir="auto">
                    {sample}
                  </span>
                ) : (
                  <span className="text-warning">{t("campaigns.content.sampleMissing")}</span>
                )}
              </p>
              {coverageMatches && coverage ? (
                <CampaignCoverageIndicator entry={coverage} label={mappingEntryLabel(t, candidate)} />
              ) : (
                <p className="m-0 text-[0.7rem] text-subtle">{t("campaigns.content.coverageAfterSave")}</p>
              )}
            </div>
          ) : null}
        </div>

        <AlertDialogActions className={contactsModalFooterClass}>
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t("campaigns.actions.cancel")}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!candidate || !canEdit}
            onClick={save}
            data-testid="campaign-variable-source-save"
          >
            {t("campaigns.content.applyVariable")}
          </Button>
        </AlertDialogActions>
      </CampaignModalContent>
    </AlertDialog>
  );
}
